import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";
import {
  getEmailSettings,
  updateEmailSettings,
  testEmailSettings,
  EmailProviderType,
  IEmailSettings,
} from "../../api/emailSettings";

interface Props {
  programId: string;
}

export default function AdminEmailSettings({ programId }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [provider, setProvider] = useState<EmailProviderType>("default");
  const [apiKey, setApiKey] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [testRecipient, setTestRecipient] = useState("");

  const [feedback, setFeedback] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    if (!programId) return;
    setLoading(true);
    getEmailSettings(programId)
      .then((data) => {
        setProvider(data.provider || "default");
        setApiKey(data.apiKey || "");
        setFromEmail(data.fromEmail || "");
        setFromName(data.fromName || "");
      })
      .catch(() => setFeedback({ type: "error", msg: "Failed to load email settings" }))
      .finally(() => setLoading(false));
  }, [programId]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const settings: IEmailSettings = { provider };
      if (provider !== "default") {
        settings.apiKey = apiKey;
        settings.fromEmail = fromEmail;
        settings.fromName = fromName;
      }
      const updated = await updateEmailSettings(programId, settings);
      setApiKey(updated.apiKey || "");
      setFromEmail(updated.fromEmail || "");
      setFromName(updated.fromName || "");
      setProvider(updated.provider || "default");
      setFeedback({ type: "success", msg: "Email settings saved" });
    } catch (err) {
      setFeedback({ type: "error", msg: err?.response?.data?.message || "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testRecipient) return;
    setTesting(true);
    setFeedback(null);
    try {
      const result = await testEmailSettings(programId, testRecipient);
      setFeedback({ type: "success", msg: result.message || "Test email sent" });
    } catch (err) {
      setFeedback({ type: "error", msg: err?.response?.data?.message || "Test failed" });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" style={{ marginBottom: 16, opacity: 0.7 }}>
        Configure a custom email provider for this program, or use the system default.
      </Typography>

      <TextField
        select
        label="Email Provider"
        value={provider}
        onChange={(e) => {
          setProvider(e.target.value as EmailProviderType);
          if (e.target.value === "default") {
            setApiKey("");
            setFromEmail("");
            setFromName("");
          }
        }}
        variant="outlined"
        size="small"
        fullWidth
        style={{ marginBottom: 16 }}
      >
        <MenuItem value="default">System Default</MenuItem>
        <MenuItem value="sendgrid">SendGrid</MenuItem>
        <MenuItem value="brevo">Brevo</MenuItem>
      </TextField>

      {provider !== "default" && (
        <>
          <TextField
            label="API Key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            style={{ marginBottom: 16 }}
            placeholder={provider === "brevo" ? "xkeysib-..." : "SG...."}
          />
          <TextField
            label="From Email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            style={{ marginBottom: 16 }}
            placeholder="notifications@yourdomain.com"
          />
          <TextField
            label="From Name"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            style={{ marginBottom: 16 }}
            placeholder="Your Organization"
          />
        </>
      )}

      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        disabled={saving}
        style={{ marginRight: 12, marginBottom: 16 }}
      >
        {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
      </Button>

      {provider !== "default" && (
        <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 16 }}>
          <TextField
            label="Test Recipient Email"
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            variant="outlined"
            size="small"
            style={{ flex: 1 }}
            placeholder="test@example.com"
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={handleTest}
            disabled={testing || !testRecipient}
          >
            {testing ? <CircularProgress size={20} /> : "Send Test"}
          </Button>
        </Box>
      )}

      {feedback && (
        <Typography
          variant="body2"
          style={{
            color: feedback.type === "success" ? "#2e7d32" : "#c62828",
            marginTop: 8,
          }}
        >
          {feedback.msg}
        </Typography>
      )}
    </Box>
  );
}
